/**
 * BT-049 — Client API backend staging.
 */
import { BACKEND_AUTH_KEY, getBackendApiUrl } from "@/config/backendStaging049.js";

function loadStoredAuth() {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(BACKEND_AUTH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getBackendToken() {
  return loadStoredAuth()?.token || null;
}

export function persistBackendAuth(payload) {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(BACKEND_AUTH_KEY, JSON.stringify(payload));
  }
  window.dispatchEvent(new Event("karate-backend-auth-updated"));
}

export function clearBackendAuth() {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(BACKEND_AUTH_KEY);
  }
  window.dispatchEvent(new Event("karate-backend-auth-updated"));
}

export function getStoredBackendUser() {
  return loadStoredAuth()?.user || null;
}

export async function backendFetch(path, options = {}) {
  const base = getBackendApiUrl();
  if (!base) throw new Error("BACKEND_NOT_CONFIGURED");
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  const token = getBackendToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${base}${path}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || data.messageFr || data.error || "API_ERROR");
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

export async function apiLogin(identifier, password, locale = "fr") {
  return backendFetch("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ identifier, password, locale }),
  });
}

export async function apiLogout() {
  try {
    await backendFetch("/api/v1/auth/logout", { method: "POST" });
  } catch {
    /* token may already be invalid */
  }
  clearBackendAuth();
}

export async function apiChangePassword({ currentPassword, newPassword, confirmPassword, locale = "fr" }) {
  return backendFetch("/api/v1/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword, confirmPassword, locale }),
  });
}

export async function apiAuthStatus() {
  return backendFetch("/api/v1/auth/status");
}

export async function apiFetchDojos() {
  return backendFetch("/api/v1/dojos");
}

export async function apiFetchStudents() {
  return backendFetch("/api/v1/students");
}

export async function apiFetchInstructeurs() {
  return backendFetch("/api/v1/instructeurs");
}

export async function apiFetchStats() {
  return backendFetch("/api/v1/stats");
}

export async function apiFetchProfile() {
  return backendFetch("/api/v1/profile");
}

export async function apiFetchMe() {
  return backendFetch("/api/v1/auth/me");
}

export async function apiFetchAvatarDiagnostics() {
  return backendFetch("/api/v1/avatar-diagnostics/me");
}

export async function apiRepairAvatarFromDisk() {
  return backendFetch("/api/v1/avatar-diagnostics/repair/me", { method: "POST" });
}

export async function apiFetchParents() {
  return backendFetch("/api/v1/parents");
}

export async function apiFetchAuditLogs() {
  return backendFetch("/api/v1/audit-logs");
}

export async function apiHealthCheck() {
  const base = getBackendApiUrl();
  if (!base) return { ok: false, reason: "not_configured" };
  try {
    const res = await fetch(`${base}/health`);
    if (!res.ok) return { ok: false, reason: "unhealthy" };
    const body = await res.json();
    return { ok: true, body };
  } catch {
    return { ok: false, reason: "unreachable" };
  }
}
